// ---------------------------------------------------------------------------
// PWA Caisse Tablette — "FERME" overlay (non-dismissible)
// Blocks the PWA when the establishment is closed per opening hours.
// Renders as a portal on top of everything (z-[9999]).
// ---------------------------------------------------------------------------

import { createPortal } from 'react-dom';
import { useCashStore } from '@/store/cash-store';

/**
 * Format a date string (YYYY-MM-DD) to DD/MM display.
 */
function formatDate(dateStr?: string): string {
  if (!dateStr) return '';
  try {
    const [, m, d] = dateStr.split('-');
    return `${d}/${m}`;
  } catch {
    return dateStr;
  }
}

export default function FermeOverlay() {
  const fermeData = useCashStore((s) => s.fermeData);

  const nextEvent = fermeData?.next_event;
  const specialDay = fermeData?.special_day;
  const localTime = fermeData?.local_time ?? '--:--';

  return createPortal(
    <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-surface">
      <div className="text-center px-8 max-w-md animate-fade-in">
        {/* Clock icon */}
        <div className="text-6xl mb-4" role="img" aria-label="Horloge">
          <svg width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" className="mx-auto text-gray-400">
            <circle cx="12" cy="12" r="10" />
            <polyline points="12 6 12 12 16 14" />
          </svg>
        </div>

        {/* Title */}
        <h1 className="text-3xl font-bold text-white mb-4 tracking-wider">
          FERME
        </h1>

        {/* Special day */}
        {specialDay && (
          <div className="bg-accent/20 text-accent rounded-xl px-4 py-2 mb-4 text-sm font-medium">
            {specialDay.label ?? 'Jour special'}
          </div>
        )}

        {/* Next opening */}
        {nextEvent && (
          <div className="bg-surface-card rounded-xl px-6 py-4 mb-4">
            <p className="text-gray-400 text-xs mb-1">Prochaine ouverture</p>
            <p className="text-white text-xl font-semibold">
              {nextEvent.time}
              {nextEvent.date && (
                <span className="text-gray-400 text-sm ml-2">
                  ({formatDate(nextEvent.date)})
                </span>
              )}
            </p>
            {nextEvent.label && (
              <p className="text-accent text-sm mt-1">{nextEvent.label}</p>
            )}
          </div>
        )}

        {/* Message */}
        <p className="text-gray-400 text-sm mb-3">
          Etablissement ferme selon le planning.
        </p>

        {/* Local time */}
        <p className="text-gray-500 text-xs mb-6">
          Heure locale : {localTime}
        </p>

        {/* Hint */}
        <p className="text-gray-500 text-xs">
          Contactez un responsable si necessaire.
        </p>
      </div>
    </div>,
    document.body,
  );
}
